import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Users, Info, Home } from 'lucide-react';
import { getSettlement, getLocation } from '../config/midgardData';
import { getLocationIcon, getLocationTypeLabel } from '../config/worldUtils';
import { useAuth } from '../context/AuthContext';

/**
 * LocationPage - Individual location view with artwork integration
 * 
 * Features:
 * - Artwork-based visual design (Town Square approved artwork)
 * - Dynamic traveler presence overlay within artwork's blank area
 * - Transparent hitbox buttons over artwork regions
 * - Responsive layout preserving artwork aspect ratio
 * - Accessibility with proper ARIA labels
 */

// Location artwork mappings - add new location artworks here
const LOCATION_ARTWORKS = {
  'thornhaven-square': {
    webp: '/assets/maps/town-square-location.webp',
    webpMobile: '/assets/maps/town-square-location-mobile.webp',
    png: '/assets/maps/town-square-location.png',
    aspectRatio: 1491 / 1055, // width / height
    // Percentage-based positions for interactive overlays (relative to artwork container)
    // Coordinates from image analysis of the artwork
    regions: {
      // Midgard medallion clickable area (top-left corner)
      midgard: { left: 3, top: 2, width: 12, height: 8 },
      // Breadcrumb: Thornhaven text (bottom breadcrumb bar, left side)
      thornhaven: { left: 15, top: 89, width: 12, height: 4 },
      // Breadcrumb: Town Square text (after Thornhaven in breadcrumb bar)
      townSquare: { left: 28, top: 89, width: 14, height: 4 },
      // Travel Here button - positioned over the green TRAVEL HERE button in breadcrumb bar
      // Measured from town-square-location.png (1491x1055): green fill x991-1405, y734-791,
      // extended to the diamond end-caps x962-1440, y728-797
      travelHere: { left: 64.52, top: 68.99, width: 32.06, height: 6.54 },
      // Travelers Present content area - below the label, full width brown area
      travelersContent: { left: 10, top: 87, width: 80, height: 10 },
    }
  }
};

// Default/fallback for locations without artwork
const DEFAULT_ARTWORK = null;

export default function LocationPage() {
  const { settlementId, locationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [settlement, setSettlement] = useState(null);
  const [location, setLocation] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // Presence state - includes current user
  const [presentUsers, setPresentUsers] = useState([]);

  // Check for mobile viewport and track container width
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      setContainerWidth(window.innerWidth);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const foundSettlement = getSettlement(settlementId);
    const foundLocation = getLocation(settlementId, locationId);
    
    if (foundSettlement && foundLocation && foundLocation.unlocked) {
      setSettlement(foundSettlement);
      setLocation(foundLocation);
      
      // Simulate user being present when viewing location
      if (user) {
        // In production, this would be fetched from presence API
        setPresentUsers([{
          id: user.id,
          displayName: user.display_name,
          isCurrentUser: true
        }]);
      }
    } else {
      navigate(`/world/midgard/settlement/${settlementId}`);
    }
  }, [settlementId, locationId, navigate, user]);

  const handleBack = useCallback(() => {
    navigate(`/world/midgard/settlement/${settlementId}`);
  }, [navigate, settlementId]);

  const handleNavigateToMidgard = useCallback(() => {
    navigate('/world/midgard');
  }, [navigate]);

  const handleNavigateToSettlement = useCallback(() => {
    navigate(`/world/midgard/settlement/${settlementId}`);
  }, [navigate, settlementId]);

  const handleTravelHere = useCallback(() => {
    navigate(`/world/midgard/play/${settlementId}/${locationId}?travel=fastTravel`);
  }, [navigate, settlementId, locationId]);

  if (!settlement || !location) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-amber-900/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Get artwork config for this location
  const artwork = LOCATION_ARTWORKS[locationId] || DEFAULT_ARTWORK;
  const Icon = getLocationIcon(location.type);
  
  // Calculate traveler count (current user counts as 1)
  const totalTravelers = presentUsers.length;
  const otherTravelers = presentUsers.filter(u => !u.isCurrentUser);

  // If no artwork available, render fallback UI
  if (!artwork) {
    return <FallbackLocationPage 
      settlement={settlement} 
      location={location} 
      user={user}
      presentUsers={presentUsers}
      onBack={handleBack}
      onTravelHere={handleTravelHere}
      showInfo={showInfo}
      setShowInfo={setShowInfo}
    />;
  }

  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden"
      style={{ backgroundColor: '#0d0906' }}
      data-testid="location-page"
    >
      {/* Accessibility: Screen reader content */}
      <div className="sr-only">
        <h1>Location: {location.name}</h1>
        <p>Settlement: {settlement.name}</p>
        <p>Realm: Midgard</p>
        <p>Travelers Present: {totalTravelers} of {location.capacity}</p>
      </div>

      {/* Loading overlay */}
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0d0906] z-50">
          <div className="w-16 h-16 border-4 border-amber-900/30 border-t-amber-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Main artwork container - matches WorldMapPage and MidgardRegionalMap centering */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pt-16 pb-8">
        {/* Aspect ratio wrapper - ensures overlays stay anchored to image bounds */}
        <div 
          className="relative w-full max-w-5xl"
          style={{ 
            maxHeight: '85vh',
            aspectRatio: `${artwork.aspectRatio}`,
          }}
        >
          {/* Background artwork image - fills the aspect-ratio container exactly */}
          <picture>
            <source 
              media="(max-width: 768px)" 
              srcSet={artwork.webpMobile}
              type="image/webp"
            />
            <source 
              srcSet={artwork.webp}
              type="image/webp"
            />
            <img
              src={artwork.png}
              alt={`${location.name} - ${settlement.name}, Midgard`}
              className="absolute inset-0 w-full h-full"
              onLoad={() => setImageLoaded(true)}
              loading="eager"
              fetchpriority="high"
            />
          </picture>

          {/* Interactive overlays - positioned over artwork regions */}
          {imageLoaded && (
            <>
              {/* Midgard clickable area (top-left) */}
              <button
                onClick={handleNavigateToMidgard}
                className="absolute cursor-pointer hover:bg-white/5 transition-colors rounded"
                style={{
                  left: `${artwork.regions.midgard.left}%`,
                  top: `${artwork.regions.midgard.top}%`,
                  width: `${artwork.regions.midgard.width}%`,
                  height: `${artwork.regions.midgard.height}%`,
                }}
                aria-label="Navigate to Midgard regional map"
                data-testid="breadcrumb-midgard"
              />

              {/* Thornhaven breadcrumb hitbox */}
              <button
                onClick={handleNavigateToSettlement}
                className="absolute cursor-pointer hover:bg-white/5 transition-colors rounded"
                style={{
                  left: `${artwork.regions.thornhaven.left}%`,
                  top: `${artwork.regions.thornhaven.top}%`,
                  width: `${artwork.regions.thornhaven.width}%`,
                  height: `${artwork.regions.thornhaven.height}%`,
                }}
                aria-label={`Navigate to ${settlement.name}`}
                data-testid="breadcrumb-settlement"
              />

              {/* Travel Here button hitbox - with subtle animated glow */}
              <button
                onClick={handleTravelHere}
                className="absolute cursor-pointer rounded-lg travel-here-glow"
                style={{
                  left: `${artwork.regions.travelHere.left}%`,
                  top: `${artwork.regions.travelHere.top}%`,
                  width: `${artwork.regions.travelHere.width}%`,
                  height: `${artwork.regions.travelHere.height}%`,
                }}
                aria-label="Travel Here - Enter the 3D world at this location"
                data-testid="travel-here-btn"
              />

              {/* Dynamic Travelers Content - overlaid on the blank dark panel */}
              <div
                className="absolute flex items-center justify-center px-1 overflow-hidden"
                style={{
                  left: `${artwork.regions.travelersContent.left}%`,
                  top: `${artwork.regions.travelersContent.top}%`,
                  width: `${artwork.regions.travelersContent.width}%`,
                  height: `${artwork.regions.travelersContent.height}%`,
                }}
                data-testid="travelers-overlay"
              >
                <DynamicTravelersContent 
                  presentUsers={presentUsers}
                  totalTravelers={totalTravelers}
                  capacity={location.capacity}
                  isMobile={isMobile}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* CSS for Travel Here glow animation */}
      <style>{`
        @keyframes travelGlow {
          0%, 100% { 
            box-shadow: 0 0 8px 2px rgba(76, 175, 80, 0.3), inset 0 0 4px rgba(76, 175, 80, 0.1);
          }
          50% { 
            box-shadow: 0 0 16px 4px rgba(76, 175, 80, 0.5), inset 0 0 8px rgba(76, 175, 80, 0.2);
          }
        }
        .travel-here-glow {
          animation: travelGlow 2s ease-in-out infinite;
        }
        .travel-here-glow:hover {
          box-shadow: 0 0 20px 6px rgba(76, 175, 80, 0.6), inset 0 0 10px rgba(76, 175, 80, 0.3);
          animation: none;
        }
      `}</style>

      {/* Mobile-only back button */}
      {isMobile && (
        <div className="fixed top-4 left-4 z-40 flex items-center gap-2">
          <button
            onClick={() => navigate('/home')}
            className="p-2 rounded-full bg-black/60 backdrop-blur-sm border border-amber-900/40"
            aria-label="Return to Social Feed"
            data-testid="mobile-home-btn"
          >
            <Home className="w-5 h-5 text-amber-100" />
          </button>
          <button
            onClick={handleBack}
            className="p-2 rounded-full bg-black/60 backdrop-blur-sm border border-amber-900/40"
            aria-label="Go back"
            data-testid="mobile-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-amber-100" />
          </button>
        </div>
      )}

      {/* Desktop navigation - top left */}
      {!isMobile && (
        <div className="fixed top-4 left-4 z-40 flex items-center gap-2">
          <button
            onClick={() => navigate('/home')}
            className="p-2 rounded-full bg-black/60 backdrop-blur-sm border border-amber-900/40 hover:bg-black/80 transition-colors"
            aria-label="Return to Social Feed"
            data-testid="desktop-home-btn"
          >
            <Home className="w-5 h-5 text-amber-100" />
          </button>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-3 py-2 rounded-full bg-black/60 backdrop-blur-sm border border-amber-900/40 hover:bg-black/80 transition-colors"
            aria-label="Go back to settlement"
            data-testid="desktop-back-btn"
          >
            <ArrowLeft className="w-4 h-4 text-amber-100" />
            <span className="text-amber-100 text-sm">{settlement.name}</span>
          </button>
        </div>
      )}

      {/* Info button */}
      <button
        onClick={() => setShowInfo(!showInfo)}
        className="fixed top-4 right-4 z-40 p-2 rounded-full bg-black/60 backdrop-blur-sm border border-amber-900/40 hover:bg-black/80 transition-colors"
        aria-label="Show location information"
        data-testid="location-info-btn"
      >
        <Info className="w-5 h-5 text-amber-100" />
      </button>

      {/* Info Panel */}
      {showInfo && (
        <div 
          className="fixed top-14 right-4 z-50 w-72 p-4 rounded-xl animate-fadeIn"
          style={{
            backgroundColor: 'rgba(26, 16, 8, 0.95)',
            border: '2px solid rgba(139, 90, 43, 0.5)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon className="w-4 h-4 text-amber-500" />
            <span className="text-xs uppercase tracking-wider text-amber-500">
              {getLocationTypeLabel(location.type)}
            </span>
          </div>
          <h3 className="font-heading text-amber-100 text-lg mb-2">{location.name}</h3>
          <p className="text-sm text-amber-100/70 leading-relaxed">
            {location.description}
          </p>
          <div className="mt-3 pt-3 border-t space-y-1" style={{ borderColor: 'rgba(139, 90, 43, 0.3)' }}>
            <p className="text-xs text-amber-500/70">Capacity: {location.capacity} travelers</p>
            <p className="text-xs text-amber-500/70">In: {settlement.name}, Midgard</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * DynamicTravelersContent - Rendered within the artwork's blank panel
 * Styled to match the carved bronze/gold Norse aesthetic
 * Compact sizing to fit naturally within the artwork
 */
function DynamicTravelersContent({ presentUsers, totalTravelers, capacity, isMobile }) {
  if (totalTravelers === 0) {
    return (
      <div className="w-full text-center">
        <p 
          className="text-[10px] md:text-xs italic tracking-wide"
          style={{ 
            color: 'rgba(180, 140, 90, 0.6)',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
          }}
        >
          No other travelers are presently here.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-wrap items-center justify-center gap-2 md:gap-3">
      {/* Player count - compact bronze/gold style */}
      <div 
        className="flex items-center gap-1 px-2 py-0.5 rounded-sm"
        style={{ 
          background: 'linear-gradient(180deg, rgba(180,130,70,0.2) 0%, rgba(120,80,40,0.15) 100%)',
          border: '1px solid rgba(180,130,70,0.35)',
        }}
      >
        <span 
          className="text-[10px] md:text-xs font-semibold"
          style={{ 
            color: '#c9a55a',
            textShadow: '0 1px 1px rgba(0,0,0,0.5)'
          }}
        >
          {totalTravelers}
        </span>
        <span className="text-[10px] md:text-xs" style={{ color: 'rgba(180,140,90,0.5)' }}>/</span>
        <span className="text-[10px] md:text-xs" style={{ color: 'rgba(180,140,90,0.6)' }}>{capacity}</span>
      </div>
      
      {/* Traveler entries - compact carved metal tag style */}
      {presentUsers.map((traveler) => (
        <div 
          key={traveler.id}
          className="flex items-center gap-1 px-2 py-0.5 rounded-sm"
          style={{ 
            background: traveler.isCurrentUser 
              ? 'linear-gradient(180deg, rgba(180,130,70,0.25) 0%, rgba(140,90,50,0.2) 100%)'
              : 'linear-gradient(180deg, rgba(60,50,40,0.3) 0%, rgba(40,30,20,0.2) 100%)',
            border: `1px solid ${traveler.isCurrentUser ? 'rgba(200,160,90,0.4)' : 'rgba(100,80,50,0.25)'}`,
          }}
        >
          {/* Mini avatar - compact embossed circle */}
          <div 
            className="w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ 
              background: traveler.isCurrentUser 
                ? 'linear-gradient(135deg, rgba(200,160,90,0.35) 0%, rgba(140,100,50,0.25) 100%)'
                : 'linear-gradient(135deg, rgba(80,60,40,0.4) 0%, rgba(50,40,30,0.3) 100%)',
              border: `1px solid ${traveler.isCurrentUser ? 'rgba(200,160,90,0.4)' : 'rgba(80,60,40,0.3)'}`,
            }}
          >
            <span 
              className="text-[8px] md:text-[10px] font-semibold"
              style={{ 
                color: traveler.isCurrentUser ? '#d4b068' : '#a08060',
              }}
            >
              {traveler.displayName?.charAt(0)?.toUpperCase() || 'T'}
            </span>
          </div>
          
          {/* Name - compact */}
          <span 
            className="text-[10px] md:text-xs truncate max-w-[60px] md:max-w-[100px]"
            style={{ 
              color: traveler.isCurrentUser ? '#d4b068' : '#a89070',
            }}
          >
            {traveler.displayName || 'Traveler'}
          </span>

          {/* "You" indicator */}
          {traveler.isCurrentUser && (
            <span 
              className="text-[8px] md:text-[10px] italic"
              style={{ color: '#8fad5a' }}
            >
              (You)
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * FallbackLocationPage - Used when no artwork is available
 */
function FallbackLocationPage({ settlement, location, user, presentUsers, onBack, onTravelHere, showInfo, setShowInfo }) {
  const Icon = getLocationIcon(location.type);
  
  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden bg-gradient-to-br from-amber-950 via-stone-900 to-amber-950"
      data-testid="location-page-fallback"
    >
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50 transition-all"
        >
          <ArrowLeft className="w-5 h-5 text-amber-100" />
          <span className="text-amber-100 text-sm hidden sm:inline">{settlement.name}</span>
        </button>

        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30">
          <Icon className="w-5 h-5 text-amber-500" />
          <span className="font-heading text-amber-100 text-sm uppercase tracking-wider">{location.name}</span>
        </div>

        <button
          onClick={() => setShowInfo(!showInfo)}
          className="p-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30 hover:border-amber-500/50"
        >
          <Info className="w-5 h-5 text-amber-100" />
        </button>
      </header>

      {/* Main content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 pt-20 pb-8">
        <div className="flex-1 w-full max-w-4xl flex flex-col items-center justify-center">
          <div 
            className="w-32 h-32 rounded-3xl flex items-center justify-center mb-8"
            style={{ backgroundColor: 'rgba(180, 130, 70, 0.15)', border: '3px solid rgba(180, 130, 70, 0.4)' }}
          >
            <Icon className="w-16 h-16 text-amber-500" />
          </div>

          <h1 className="font-heading text-3xl md:text-4xl text-white text-center mb-3">
            {location.name}
          </h1>

          <p className="text-amber-100/70 text-center max-w-lg text-sm md:text-base mb-8 px-4">
            {location.description}
          </p>

          <div className="flex items-center gap-2 text-xs text-amber-500/60 mb-6">
            <span>Midgard</span>
            <span>→</span>
            <span>{settlement.name}</span>
            <span>→</span>
            <span className="text-amber-400">{location.name}</span>
          </div>

          <button
            onClick={onTravelHere}
            className="flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold shadow-lg hover:from-emerald-500 hover:to-emerald-600 transition-all hover:scale-105"
            data-testid="travel-here-btn"
          >
            <span>Travel Here</span>
          </button>
        </div>

        {/* Users Present */}
        <div className="w-full max-w-2xl">
          <div className="bg-black/40 backdrop-blur-sm rounded-2xl border border-amber-900/30 overflow-hidden">
            <div className="p-4 border-b border-amber-900/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" />
                <span className="font-medium text-amber-100">Travelers Present</span>
              </div>
              <span className="text-sm text-amber-500/70">
                {presentUsers.length} / {location.capacity}
              </span>
            </div>

            <div className="p-4">
              {presentUsers.length > 0 ? (
                <div className="space-y-2">
                  {presentUsers.map((traveler, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-black/30">
                      <div className="w-8 h-8 rounded-full bg-amber-900/50 flex items-center justify-center">
                        <span className="text-xs text-amber-200">{traveler.displayName?.charAt(0) || 'T'}</span>
                      </div>
                      <span className="text-sm text-amber-100">{traveler.displayName}</span>
                      {traveler.isCurrentUser && <span className="text-xs text-emerald-400">(You)</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-amber-100/50 text-sm">No travelers here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
